import { Users, Plus, Download } from 'lucide-react';

import UserAvatar from './UserAvatar';
import UserRoleChip from './UserRoleChip';
import UserStatusPill from './UserStatusPill';

interface UsersEmptyStateProps {
  selfName: string;
  selfEmail: string | null;
}

/** First-run state shown when the tenant has only one user (the viewer). */
export default function UsersEmptyState({ selfName, selfEmail }: UsersEmptyStateProps) {
  return (
    <section className="flex-1 min-h-0 bg-white dark:bg-[#1a1f1c] rounded-xl border border-stone-200 dark:border-stone-800 flex flex-col items-center justify-center text-center p-10">
      <div className="w-24 h-24 rounded-3xl bg-[#EAF3DE] text-moss-700 flex items-center justify-center mb-4">
        <Users size={42} strokeWidth={1.25} />
      </div>
      <h2 className="text-3xl font-bold uppercase tracking-[.01em] text-bark-900 dark:text-[#e8ece9] leading-none m-0">
        Just you so far
      </h2>
      <p className="mt-3 max-w-[460px] text-sm text-stone-600 dark:text-stone-400 leading-relaxed">
        Bonsai Bar runs on a small team. You&apos;re the first user in this tenant. Invite a
        Regional Director to start scoping users to a region, or import staff from FareHarbor to
        backfill instructors.
      </p>
      <div className="flex gap-2.5 mt-5">
        <button
          type="button"
          disabled
          title="Auth0 invite flow ships in a follow-up"
          className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-lg text-[13px] font-semibold text-white bg-moss-600 opacity-70 cursor-not-allowed"
        >
          <Plus size={13} />
          Invite your first user
        </button>
        <button
          type="button"
          disabled
          title="FareHarbor import lives in Staff today"
          className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-[#1a1f1c] text-[13px] font-semibold text-stone-400 dark:text-stone-500 cursor-not-allowed"
        >
          <Download size={13} />
          Import from FareHarbor
        </button>
      </div>

      {/* You */}
      <div className="mt-8 w-[460px] bg-stone-50 dark:bg-[#1e2520] border border-stone-200 dark:border-stone-800 rounded-lg p-3 flex items-center gap-3">
        <UserAvatar name={selfName} size={36} />
        <div className="flex-1 text-left">
          <div className="text-[13px] font-bold text-bark-900 dark:text-[#e8ece9]">
            {selfName} <span className="font-normal text-stone-500 dark:text-stone-400">(you)</span>
          </div>
          {selfEmail && (
            <div className="text-[11px] text-stone-500 dark:text-stone-400">{selfEmail}</div>
          )}
        </div>
        <UserRoleChip role="admin" />
        <UserStatusPill status="active" />
      </div>

      <div className="mt-6 text-[11px] text-stone-500 dark:text-stone-400 max-w-[460px] leading-relaxed">
        Bulk import (CSV), SSO with Google Workspace and custom roles are on the v2 roadmap.
      </div>
    </section>
  );
}
